/**
 * Attendance print component - Print button with dropdown menu and settings
 */

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Printer, CheckSquare, Square } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { PrintSettings } from "../types";

interface Klient {
  id: number;
  alias: string;
}

interface AttendancePrintProps {
  selectedKlientId: number | null;
  klienti?: Klient[];
  printSettings: PrintSettings;
  onPrintSettingsChange: (settings: PrintSettings) => void;
  areAllColumnsSelected: boolean;
  onToggleAllColumns: (checked: boolean) => void;
  onPrintAllClients?: () => void;
  onPrintSelectedClient: () => void;
}

export function AttendancePrint({
  selectedKlientId,
  klienti,
  printSettings,
  onPrintSettingsChange,
  areAllColumnsSelected,
  onToggleAllColumns,
  onPrintAllClients,
  onPrintSelectedClient,
}: AttendancePrintProps) {
  const { t } = useTranslation();

  const updateSetting = (key: keyof PrintSettings, value: boolean) => {
    onPrintSettingsChange({ ...printSettings, [key]: value });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Printer className="h-4 w-4 mr-2" />
          {t("attendance:print.print")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {onPrintAllClients && (
          <DropdownMenuItem onClick={onPrintAllClients}>
            {t("attendance:print.printAllClients")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={onPrintSelectedClient}
          disabled={!selectedKlientId}
          className={!selectedKlientId ? "opacity-50 cursor-not-allowed" : ""}
        >
          {t("attendance:print.printSelectedClient")}
          {selectedKlientId && (
            <span className="ml-2 text-xs text-muted-foreground">
              {klienti?.find(k => k.id === selectedKlientId)?.alias}
            </span>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onToggleAllColumns(!areAllColumnsSelected)}
        >
          <div className="flex items-center gap-2">
            {areAllColumnsSelected ? (
              <CheckSquare className="h-4 w-4" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            {t("attendance:print.selectAllColumns")}
          </div>
        </DropdownMenuItem>
        <div className="max-h-60 overflow-y-auto p-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-date"
                checked={printSettings.showDate}
                onCheckedChange={checked =>
                  updateSetting("showDate", !!checked)
                }
              />
              <Label htmlFor="print-date" className="cursor-pointer text-sm">
                {t("attendance:print.columns.date")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-time"
                checked={printSettings.showTime}
                onCheckedChange={checked =>
                  updateSetting("showTime", !!checked)
                }
              />
              <Label htmlFor="print-time" className="cursor-pointer text-sm">
                {t("attendance:print.columns.time")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-client"
                checked={printSettings.showClient}
                onCheckedChange={checked =>
                  updateSetting("showClient", !!checked)
                }
              />
              <Label htmlFor="print-client" className="cursor-pointer text-sm">
                {t("attendance:print.columns.client")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-duration"
                checked={printSettings.showDuration}
                onCheckedChange={checked =>
                  updateSetting("showDuration", !!checked)
                }
              />
              <Label
                htmlFor="print-duration"
                className="cursor-pointer text-sm"
              >
                {t("attendance:print.columns.duration")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-services"
                checked={printSettings.showTrainingType}
                onCheckedChange={checked =>
                  updateSetting("showTrainingType", !!checked)
                }
              />
              <Label
                htmlFor="print-services"
                className="cursor-pointer text-sm"
              >
                {t("attendance:print.columns.services")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-price"
                checked={printSettings.showPrice}
                onCheckedChange={checked =>
                  updateSetting("showPrice", !!checked)
                }
              />
              <Label htmlFor="print-price" className="cursor-pointer text-sm">
                {t("attendance:print.columns.price")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-notes"
                checked={printSettings.showNotes}
                onCheckedChange={checked =>
                  updateSetting("showNotes", !!checked)
                }
              />
              <Label htmlFor="print-notes" className="cursor-pointer text-sm">
                {t("attendance:print.columns.notes")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-totals"
                checked={printSettings.showTotals}
                onCheckedChange={checked =>
                  updateSetting("showTotals", !!checked)
                }
              />
              <Label htmlFor="print-totals" className="cursor-pointer text-sm">
                {t("attendance:print.columns.showTotals")}
              </Label>
            </div>
            <div className="flex items-center gap-2 p-1 hover:bg-accent rounded">
              <Checkbox
                id="print-date-range"
                checked={printSettings.includeDateRange}
                onCheckedChange={checked =>
                  updateSetting("includeDateRange", !!checked)
                }
              />
              <Label
                htmlFor="print-date-range"
                className="cursor-pointer text-sm"
              >
                {t("attendance:print.columns.includeDateRange")}
              </Label>
            </div>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
